import React, { useState, useRef, useEffect } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
  MessageSquare, 
  Send, 
  Paperclip, 
  X, 
  Loader2, 
  AlertCircle, 
  Calendar,
  ChevronLeft,
  Plus,
  Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { compressBlogImage } from '@/lib/utils';

interface SupportTicketConsoleProps {
  onTicketCreated?: (ticketId: string) => void;
  compact?: boolean;
}

export function SupportTicketConsole({ onTicketCreated, compact = false }: SupportTicketConsoleProps) {
  const { toast } = useToast();
  
  const {
    tickets,
    activeTicket,
    replies,
    selectedTicketId,
    setSelectedTicketId,
    loading,
    submitting,
    createTicket,
    replyToTicket,
    closeTicket,
  } = useTickets();

  // Mode state: 'chat' | 'create' | 'list'
  const [mode, setMode] = useState<'chat' | 'create' | 'list'>('create');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  
  // Creation form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});
  
  // Reply form state
  const [replyText, setReplyText] = useState('');
  
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Chat scroll container ref
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Set initial mode based on whether there's an active/open ticket
  useEffect(() => {
    if (activeTicket) {
      setMode('chat');
      setSelectedTicketId(activeTicket.ticketId);
    } else if (tickets.length > 0) {
      setMode('list');
    } else {
      setMode('create');
    }
  }, [activeTicket, tickets.length, setSelectedTicketId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [replies, mode]);

  // Handle image upload and compression
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload an image file (PNG, JPG, WebP).',
        variant: 'destructive',
      });
      return;
    }

    setImageUploading(true);
    try {
      // Compress using the blog image helper (re-sizes to max 800px width, 80% quality JPEG)
      const base64Url = await compressBlogImage(file);
      setScreenshot(base64Url);
      toast({
        title: 'Screenshot processed',
        description: 'Image compressed and attached successfully.',
      });
    } catch (error) {
      console.error('Error processing screenshot:', error);
      toast({
        title: 'Compression Error',
        description: 'Could not compress the screenshot. Please try another image.',
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors: { title?: string; description?: string } = {};
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create Ticket Submission
  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const ticketId = await createTicket(title.trim(), description.trim(), screenshot || undefined);
      // Reset form
      setTitle('');
      setDescription('');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      setMode('chat');
      if (onTicketCreated) {
        onTicketCreated(ticketId);
      }
    } catch (err) {
      // toast is already handled in the hook
    }
  };

  // Send Reply Submission
  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicketId) return;

    try {
      const textToSend = replyText;
      setReplyText(''); // Clear input instantly for snappy UI
      await replyToTicket(selectedTicketId, textToSend, false);
    } catch (err) {
      // Restore input text if send failed
      setReplyText(replyText);
    }
  };

  // Close Ticket Submission
  const handleCloseActiveTicket = async () => {
    if (!selectedTicketId) return;
    if (window.confirm('Are you sure you want to close this ticket? You will not be able to send further replies.')) {
      try {
        await closeTicket(selectedTicketId);
        setMode('list');
      } catch (err) {
        // Handled in hook
      }
    }
  };

  // Find currently viewed ticket details
  const currentTicket = tickets.find(t => t.ticketId === selectedTicketId) || null;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-sm">Loading support ticket...</p>
      </div>
    );
  }

  // --- RENDERING TICKET CREATION FORM ---
  if (mode === 'create') {
    // If user has an active open ticket, they can't be in creation mode. Redirect them.
    if (activeTicket) {
      setSelectedTicketId(activeTicket.ticketId);
      setMode('chat');
      return null;
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Submit a Support Ticket
          </h3>
          {tickets.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setMode('list')}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Ticket History
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmitTicket} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="title" className="text-sm font-medium">
              Issue Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors(prev => ({ ...prev, title: undefined }));
              }}
              placeholder="Brief summary of the issue (e.g. Can't download PDF invoice)"
              className={errors.title ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.title && (
              <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.title}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="description" className="text-sm font-medium">
              Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors(prev => ({ ...prev, description: undefined }));
              }}
              placeholder="Please provide full details about your problem, including steps to reproduce it."
              rows={4}
              className={errors.description ? 'border-destructive focus-visible:ring-destructive' : ''}
            />
            {errors.description && (
              <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.description}
              </p>
            )}
          </div>

          {/* Screenshot Uploader */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Attach Screenshot (Optional)</Label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading || submitting}
                className="flex items-center gap-2"
              >
                {imageUploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Paperclip className="w-4 h-4" />
                )}
                {screenshot ? 'Change Screenshot' : 'Upload Image'}
              </Button>
              <span className="text-xs text-muted-foreground">
                PNG, JPG, WebP up to 5MB (auto-resized)
              </span>
            </div>

            {screenshot && (
              <div className="relative inline-block mt-2 border rounded-lg overflow-hidden group max-w-sm">
                <img
                  src={screenshot}
                  alt="Screenshot preview"
                  className="max-h-48 object-contain bg-slate-50"
                />
                <button
                  type="button"
                  onClick={handleRemoveScreenshot}
                  className="absolute top-1.5 right-1.5 p-1 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors shadow-md"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting || imageUploading}
            className="w-full flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit Support Ticket
          </Button>
        </form>
      </div>
    );
  }

  // --- RENDERING TICKET HISTORY LIST ---
  if (mode === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Support Ticket History
          </h3>
          {!activeTicket ? (
            <Button variant="default" size="sm" onClick={() => setMode('create')}>
              <Plus className="w-4 h-4 mr-1" />
              New Ticket
            </Button>
          ) : (
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-xs py-1">
              Active Ticket Open
            </Badge>
          )}
        </div>

        {tickets.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
            <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">You haven't submitted any support tickets yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((t) => (
              <div
                key={t.ticketId}
                onClick={() => {
                  setSelectedTicketId(t.ticketId);
                  setMode('chat');
                }}
                className={`p-4 border rounded-xl hover:border-primary cursor-pointer transition-all duration-200 bg-card flex items-center justify-between group ${
                  t.status === 'open' ? 'border-amber-200 bg-amber-50/20' : 'border-slate-100 hover:bg-slate-50/40'
                }`}
              >
                <div className="space-y-1.5 pr-4 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                      {t.title}
                    </span>
                    <Badge 
                      variant={t.status === 'open' ? 'default' : 'secondary'} 
                      className={`text-[10px] px-2 py-0.5 font-semibold capitalize ${
                        t.status === 'open' ? 'bg-emerald-500 hover:bg-emerald-500 text-white' : ''
                      }`}
                    >
                      {t.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {t.description}
                  </p>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {format(t.createdAt, 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 flex items-center justify-center p-2 rounded-lg bg-slate-50 border border-slate-100 text-slate-400 group-hover:text-primary group-hover:bg-primary/5 group-hover:border-primary/20 transition-all">
                  <ChevronLeft className="w-4 h-4 rotate-180" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- RENDERING ACTIVE TICKET CHAT WINDOW ---
  if (mode === 'chat' && currentTicket) {
    const isClosed = currentTicket.status === 'closed';

    return (
      <div className="flex flex-col space-y-4">
        {/* Chat Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8 rounded-full border-slate-200"
              onClick={() => setMode('list')}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm leading-tight text-foreground">
                  {currentTicket.title}
                </h3>
                <Badge 
                  variant={currentTicket.status === 'open' ? 'default' : 'secondary'} 
                  className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 ${
                    currentTicket.status === 'open' ? 'bg-emerald-500 hover:bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {currentTicket.status}
                </Badge>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Created {format(currentTicket.createdAt, 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>

          {!isClosed && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCloseActiveTicket}
              className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 text-xs font-medium"
            >
              Close Ticket
            </Button>
          )}
        </div>

        {/* Chat Log & Message Thread */}
        <div className={`overflow-y-auto space-y-4 pr-1 ${compact ? 'max-h-[350px] min-h-[250px]' : 'max-h-[500px] min-h-[350px]'} flex-1`}>
          {/* Issue Original Description Box */}
          <div className="p-4 rounded-xl border border-slate-100 bg-slate-50/40 text-sm space-y-3">
            <div className="space-y-1">
              <span className="font-semibold text-xs text-muted-foreground">Original Issue Description:</span>
              <p className="text-slate-800 whitespace-pre-line leading-relaxed text-sm">
                {currentTicket.description}
              </p>
            </div>
            {currentTicket.screenshotUrl && (
              <div className="space-y-1">
                <span className="font-semibold text-xs text-muted-foreground block">Attached Screenshot:</span>
                <button 
                  type="button"
                  onClick={() => setLightboxUrl(currentTicket.screenshotUrl || null)}
                  className="inline-block border rounded-lg overflow-hidden max-w-[200px] max-h-[150px] hover:ring-2 hover:ring-primary transition-all bg-white text-left focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <img
                    src={currentTicket.screenshotUrl}
                    alt="Attached screenshot"
                    className="max-w-full max-h-full object-contain cursor-zoom-in"
                  />
                </button>
              </div>
            )}
          </div>

          {/* Conversation Thread */}
          {replies.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground italic flex flex-col items-center justify-center">
              <MessageSquare className="w-5 h-5 mb-1.5 text-slate-300" />
              Waiting for a support representative to review your request...
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              {replies.map((reply) => {
                const replyIsStaff = reply.isAdmin;
                return (
                  <div
                    key={reply.replyId}
                    className={`flex flex-col max-w-[85%] ${
                      replyIsStaff ? 'mr-auto items-start' : 'ml-auto items-end'
                    }`}
                  >
                    {/* Sender Tag */}
                    <span className="text-[10px] text-muted-foreground mb-1 px-1">
                      {replyIsStaff ? '🛡️ Staff Support' : 'You'}
                    </span>
                    {/* Bubble */}
                    <div
                      className={`p-3 rounded-2xl text-sm leading-relaxed ${
                        replyIsStaff
                          ? 'bg-slate-100 text-slate-800 rounded-tl-none border border-slate-200'
                          : 'bg-primary text-primary-foreground rounded-tr-none'
                      }`}
                    >
                      <p className="whitespace-pre-line font-normal text-sm">{reply.text}</p>
                    </div>
                    {/* Date */}
                    <span className="text-[9px] text-muted-foreground mt-1 px-1">
                      {format(reply.createdAt, 'MMM d, h:mm a')}
                    </span>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        {/* Chat Footer Reply Box */}
        {isClosed ? (
          <div className="flex items-center justify-center gap-2 p-3 bg-slate-50 border border-slate-100 rounded-xl text-muted-foreground text-xs text-center font-medium">
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            This ticket has been closed. If you have more questions, please go back and open a new ticket.
          </div>
        ) : (
          <form onSubmit={handleSendReply} className="flex gap-2 items-end border-t pt-3">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a message/reply..."
              rows={2}
              className="resize-none pr-12 text-sm max-h-[100px] rounded-xl focus-visible:ring-primary flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply(e);
                }
              }}
            />
            <Button
              type="submit"
              disabled={submitting || !replyText.trim()}
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        )}

        {/* Screenshot Lightbox Dialog */}
        <Dialog open={!!lightboxUrl} onOpenChange={(open) => !open && setLightboxUrl(null)}>
          <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none flex items-center justify-center">
            <div className="relative max-h-[90vh] max-w-full overflow-hidden bg-black/95 rounded-lg flex items-center justify-center p-1 sm:p-2 border">
              <button
                type="button"
                onClick={() => setLightboxUrl(null)}
                className="absolute top-3 right-3 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-50 border border-white/10"
                title="Close image view"
              >
                <X className="w-4 h-4" />
              </button>
              {lightboxUrl && (
                <img
                  src={lightboxUrl}
                  alt="Enlarged screenshot"
                  className="max-h-[85vh] max-w-full object-contain rounded"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
}
